// @flow

import MessageFormat from 'messageformat';
import * as React from 'react';
import { IntlFormatter } from '@ta/platform.i18n';
import { getMessages as ssrCacheGetMessages } from '@ta/platform.ssr-cache';

// IMPORTANT: The compiled message cache is *shared* between customer requests in the ssr-cache!
const compiledMessageCache = ssrCacheGetMessages();

// Hack until https://jira.tamg.io/browse/WP-1911 is done
/* eslint-disable no-restricted-globals */
const getInitialState = () =>
  (SERVER ? global.__STATE__ : __WEB_CONTEXT__.pageManifest.redux)?.i18n || {};
/* eslint-enable no-restricted-globals */

let intlFormatter = null;
const initializeFormatter = () => {
  if (intlFormatter === null) {
    intlFormatter = new IntlFormatter(getInitialState().ietfLocale, getInitialState().currencyCode);
  }

  return intlFormatter;
};

const globalize = {
  formatNumber(type: 'one' | 'integer' | 'default', value: number) {
    return initializeFormatter().formatNumber(type, value);
  },
  formatPercentage(type: 'one' | 'integer' | 'default', value: number) {
    return initializeFormatter().formatPercentage(type, value);
  },
  formatCurrency(value: number) {
    return initializeFormatter().formatCurrency(value);
  },
  formatCurrencyInteger(value: number) {
    return initializeFormatter().formatCurrencyInteger(value);
  },
  formatCurrencyISO(value: number) {
    return initializeFormatter().formatCurrencyISO(value);
  },
  formatCurrencyISOInteger(value: number) {
    return initializeFormatter().formatCurrencyISOInteger(value);
  },
  formatDate(type: string, date: Date) {
    return initializeFormatter().formatDate(type, date);
  },
};

const createFormatter = locale => {
  const formatter = new MessageFormat(locale);

  formatter.addFormatters({
    number: (val, loc, args) => {
      if (args === 'percentage') {
        const type = args[1] === 'one' || args[1] === 'integer' ? args[1] : false;
        return globalize.formatPercentage(type || 'default', val);
      } else if (args === 'currency' || args[0] === 'currency') {
        if (args[1] === 'integer') {
          return globalize.formatCurrencyInteger(val);
        } else if (args[1] === 'iso_default') {
          return globalize.formatCurrencyISO(val);
        } else if (args[1] === 'iso_integer') {
          return globalize.formatCurrencyISOInteger(val);
        }
        return globalize.formatCurrency(val);
      }
      return globalize.formatNumber(args || 'default', val);
    },
    date: (val, loc, args) => globalize.formatDate(args[0] || 'default', val),
    choice: (val, loc, args) => {
      const choices = args
        .split('|')
        .map(choice => choice.split(/(#|<)/))
        .map(([bound, separator, result]) => ({
          bound: Number(bound),
          isInclusive: separator === '#',
          result,
        }));

      const value = Number(val);
      const lowerBound = ({ bound, isInclusive }) => (isInclusive ? bound <= value : bound < value);
      const upperBound = ({ bound, isInclusive }) => (isInclusive ? value < bound : value <= bound);

      return (
        choices.find(
          (choice, idx) =>
            lowerBound(choice) && (choices.length === idx + 1 || upperBound(choices[idx + 1])),
        ) || choices[0]
      ).result;
    },
  });

  return formatter;
};

let currentLocale = null;
let localeFormatter = null;
const getFormatter = locale => {
  if (locale !== currentLocale || localeFormatter === null) {
    localeFormatter = createFormatter(locale);
    currentLocale = locale;
  }
  return localeFormatter;
};

const messages = {};
const loadMessages = (newMessages: { [string]: string }) => {
  Object.entries(newMessages).forEach(([key, message]) => {
    messages[key] = message;
  });
};
const getMessage = (key: string) => messages[key] || null;

const getCompiledMessage = (key: string) => {
  const { ietfLocale } = getInitialState();

  const message = getMessage(key);
  if (message === null) {
    return null;
  }

  const cacheKey = `${ietfLocale}~${message}`;
  const existing = compiledMessageCache.get(cacheKey);
  if (existing) {
    return existing;
  }

  const messageTemplate = SERVER
    ? runInsecurely(() => getFormatter(ietfLocale).compile(message))
    : getFormatter(ietfLocale).compile(message);

  compiledMessageCache.set(cacheKey, messageTemplate);
  return messageTemplate;
};

const localize = (key: string, args?: { [string]: string | number }): string => {
  if (!args) {
    const msg = getMessage(key);
    return msg != null ? msg : key;
  }

  try {
    const messageTemplate = getCompiledMessage(key);
    if (messageTemplate === null) {
      return `${key}${args ? `: ${JSON.stringify(args)}` : ''}`;
    }

    return messageTemplate(args);
  } catch (e) {
    console.error(`Exception while localizing ${key}:`, e);
    return key;
  }
};

// eslint-disable-next-line no-restricted-globals
loadMessages((SERVER ? global.__MESSAGES__ : __WEB_CONTEXT__.pageManifest.messages) || {});

const unescaped = (text: string, className?: string) => (
  // eslint-disable-next-line react/no-danger
  <span className={className} dangerouslySetInnerHTML={{ __html: text }} />
);
export { globalize, localize, unescaped, loadMessages, getMessage };
export default localize;
